<div class="item-container">
  <div class="item-topbar">
    <div class="topbar-left">
      <h1>
        Register Items
        <small>Manage all inventory items</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i> <span>Inventory</span> / <span class="current">Register Items</span>
        <span class="separator">|</span>
        <span class="time"><i class="bi bi-clock"></i> {{ timestamp() }}</span>
      </div>
    </div>
    <div class="topbar-right">
      <div class="search-box">
        <i class="bi bi-search"></i>
        <input
          type="text"
          placeholder="Search code, name, category..."
          [ngModel]="search()"
          (ngModelChange)="search.set($event); onSearchChange()"
        />
      </div>
      <button class="btn btn-outline btn-sm" type="button" (click)="exportData()">
        <i class="bi bi-download"></i> Export
      </button>
      <button class="btn btn-primary" type="button" (click)="openItemModal()">
        <i class="bi bi-plus-circle"></i> Add New Item
      </button>
    </div>
  </div>

  <div class="stats-row">
    <div class="stat-card">
      <div class="stat-icon blue">
        <i class="bi bi-box-seam"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Total Items</div>
        <div class="stat-value">{{ stats().totalItems }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon green">
        <i class="bi bi-check-circle"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Active Items</div>
        <div class="stat-value">{{ stats().activeItems }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon amber">
        <i class="bi bi-exclamation-triangle"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Low Stock</div>
        <div class="stat-value">{{ stats().lowStock }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon purple">
        <i class="bi bi-key"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Rentable Items</div>
        <div class="stat-value">{{ stats().rentableItems }}</div>
      </div>
    </div>
  </div>

  <div class="filter-bar">
    <div class="filter-left">
      <div class="filter-group">
        <label><i class="bi bi-tags"></i> Category</label>
        <select [ngModel]="categoryFilter()" (ngModelChange)="categoryFilter.set($event); onFilterChange()">
          <option value="all">All Categories</option>
          @for (cat of categories(); track cat.id) {
            <option [value]="cat.id">{{ cat.name }}</option>
          }
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-building"></i> Branch</label>
        <select [ngModel]="branchFilter()" (ngModelChange)="branchFilter.set($event); onFilterChange()">
          @if (!branchFilterLocked()) {
            <option value="all">All Branches</option>
          }
          @for (branch of branches(); track branch.id) {
            <option [value]="branch.id">{{ branch.name }}</option>
          }
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-geo-alt"></i> Location</label>
        <select [ngModel]="locationFilter()" (ngModelChange)="locationFilter.set($event); onFilterChange()">
          <option value="all">All Locations</option>
          @for (location of locations(); track location.id) {
            <option [value]="location.id">{{ location.name }}</option>
          }
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-key"></i> Rentable</label>
        <select [ngModel]="rentableFilter()" (ngModelChange)="rentableFilter.set($event); onFilterChange()">
          <option value="all">All Items</option>
          <option value="true">Rentable Only</option>
          <option value="false">Non-Rentable Only</option>
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-check-circle"></i> Status</label>
        <select [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event); onFilterChange()">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="discontinued">Discontinued</option>
        </select>
      </div>
    </div>
    <div class="filter-right">
      <span class="filter-count">Showing {{ filteredItems().length }} items</span>
      <button class="btn btn-outline btn-sm" type="button" (click)="clearFilters()">Clear filters</button>
    </div>
  </div>

  <div class="table-wrap">
    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Item Code</th>
            <th>Item Name</th>
            <th>Category</th>
            <th>Branch</th>
            <th>Location</th>
            <th title="Total on-hand across all store locations at this branch">Branch Stock</th>
            <th>Price</th>
            <th>Rentable</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (item of paginatedItems(); track item.id) {
            <tr>
              <td><span class="item-code">{{ item.code }}</span></td>
              <td>
                <div class="item-info">
                  <span class="item-icon"><i class="bi" [class]="item.icon || 'bi-box-seam'"></i></span>
                  <span>{{ item.name }}</span>
                </div>
              </td>
              <td>{{ item.category }}</td>
              <td><span class="branch-tag">{{ item.branch || 'N/A' }}</span></td>
              <td><span class="location-tag">{{ item.location || 'N/A' }}</span></td>
              <td><span class="stock-level" [class]="stockLevelClass(item)">{{ item.stockLevel }}</span></td>
              <td>{{ formatCurrency(item.price) }}</td>
              <td>
                <span class="rentable-badge" [class.rentable]="item.isRentable" [class.not-rentable]="!item.isRentable">
                  <i class="bi" [class.bi-check-circle-fill]="item.isRentable" [class.bi-x-circle-fill]="!item.isRentable"></i>
                  {{ item.isRentable ? 'Yes' : 'No' }}
                </span>
              </td>
              <td><span class="status-badge" [class]="item.status">{{ statusLabel(item.status) }}</span></td>
              <td class="actions-cell">
                <div class="row-actions">
                  <a
                    class="btn-action track"
                    title="Track this item"
                    [routerLink]="['/item-history', item.id]"
                  >
                    <i class="bi bi-geo-alt"></i>
                  </a>
                  <button type="button" class="btn-action edit" title="Edit item" (click)="openEditModal(item)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  @if (item.status !== 'inactive') {
                    <button
                      type="button"
                      class="btn-action delete"
                      title="Remove from active catalog"
                      [disabled]="!!deletingId()"
                      (click)="deleteItem(item)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  }
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="10" class="empty-cell">No items match your search or filters.</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <div class="pagination-bar">
    <div class="pagination-info">
      Showing <span>{{ paginationStart() }}</span> to <span>{{ paginationEnd() }}</span> of <span>{{ filteredItems().length }}</span> items
    </div>
    <div class="pagination-controls">
      <button class="btn btn-outline btn-sm" [disabled]="page() === 1" (click)="goToPage(page() - 1)"><i class="bi bi-chevron-left"></i> Previous</button>
      @for (p of pageNumbers(); track p) {
        <button class="btn btn-sm" [class.btn-primary]="page() === p" [class.btn-outline]="page() !== p" (click)="goToPage(p)">{{ p }}</button>
      }
      <button class="btn btn-outline btn-sm" [disabled]="page() === totalPages()" (click)="goToPage(page() + 1)">Next <i class="bi bi-chevron-right"></i></button>
    </div>
  </div>
</div>

<!-- Add/Edit Item Modal -->
<div class="modal-overlay" [class.open]="showItemModal()" (click)="closeModalOnBackdrop($event)">
  <div class="modal">
    <div class="modal-header">
      <h2>
        <i class="bi" [class]="isEditing() ? 'bi-pencil' : 'bi-plus-circle'"></i>
        {{ isEditing() ? 'Edit Item' : 'Add New Item' }}
      </h2>
      <button class="close-btn" (click)="closeItemModal()">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="modal-body">
          <div class="naming form-group mb-2">
          <label>Item Name <span class="required">*</span></label>
          <input type="text" placeholder="e.g., Safety Helmet" [ngModel]="newItem().name" (ngModelChange)="updateNewItem('name', $event)" />
        </div>


      <div class="form-row">
        <div class="form-group">
          <label>Category <span class="required">*</span></label>
          <select
            [ngModel]="newItem().category"
            (ngModelChange)="updateNewItem('category', asSelectValue($event))"
          >
            <option [ngValue]="''">Select Category</option>
            @for (cat of categoryOptions(); track cat.id) {
              <option [ngValue]="cat.id">{{ cat.name }}</option>
            }
          </select>
        </div>
        <div class="form-group">
          <label>Unit of Measure <span class="required">*</span></label>
          <select
            [ngModel]="newItem().unit"
            (ngModelChange)="updateNewItem('unit', asSelectValue($event))"
          >
            <option [ngValue]="''">Select Unit</option>
            @for (unit of unitOptions(); track unit.id) {
              <option [ngValue]="unit.id">{{ unit.name }}</option>
            }
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Branch <span class="required">*</span></label>
          <select [ngModel]="newItem().branch" (ngModelChange)="updateNewItem('branch', $event)">
            <option value="">Select Branch</option>
            @for (branch of branches(); track branch.id) {
              <option [value]="branch.id">{{ branch.name }}</option>
            }
          </select>
        </div>
        <div class="form-group">
          <label>Store Location <span class="required">*</span></label>
          <select [ngModel]="newItem().location" (ngModelChange)="updateNewItem('location', $event)">
            <option value="">Select Location</option>
            @for (location of modalLocations(); track location.id) {
              <option [value]="location.id">{{ location.name }}</option>
            }
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Stock Level <span class="required">*</span></label>
          <input type="number" min="0" step="1" appDigitsOnly placeholder="0" [ngModel]="newItem().stockLevel" (ngModelChange)="updateNewItem('stockLevel', $event)" />
        </div>
        <div class="form-group">
          <label>Reorder Level <span class="required">*</span></label>
          <input type="number" min="0" step="1" appDigitsOnly placeholder="0" [ngModel]="newItem().reorderLevel" (ngModelChange)="updateNewItem('reorderLevel', $event)" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Price <span class="required">*</span></label>
          <input type="number" min="0" step="0.01" appDecimalOnly placeholder="0.00" [ngModel]="newItem().price" (ngModelChange)="updateNewItem('price', $event)" />
        </div>
        <div class="form-group">
          <label>Status <span class="required">*</span></label>
          <select [ngModel]="newItem().status" (ngModelChange)="updateNewItem('status', $event)">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>
      </div>

      <!-- Rentable Toggle -->
      <div class="form-row">
        <div class="form-group full">
          <div class="toggle-label">
            <span class="toggle-text">This item is rentable</span>
            <label class="toggle-switch large">
              <input type="checkbox" [ngModel]="newItem().isRentable" (ngModelChange)="updateNewItem('isRentable', $event)" />
              <span class="toggle-slider"></span>
            </label>
          </div>
          <small class="hint">Enable if this item can be rented out to customers</small>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn btn-outline" (click)="closeItemModal()">Cancel</button>
      <button class="btn btn-primary" (click)="submitItem()">
        <i class="bi bi-check-lg"></i> {{ isEditing() ? 'Save Changes' : 'Add Item' }}
      </button>
    </div>
  </div>
</div>

@if (showToast()) {
  <div class="toast-container show">
    <div
      class="toast"
      [class.success]="toastType() === 'success'"
      [class.error]="toastType() === 'error'"
      [class.info]="toastType() === 'info'"
    >
      <div class="toast-icon">
        @if (toastType() === 'error') {
          <i class="bi bi-x-circle-fill"></i>
        } @else if (toastType() === 'info') {
          <i class="bi bi-info-circle-fill"></i>
        } @else {
          <i class="bi bi-check-circle-fill"></i>
        }
      </div>
      <div class="toast-content">
        <div class="toast-title">
          {{ toastType() === 'error' ? 'Error' : toastType() === 'info' ? 'Notice' : 'Success!' }}
        </div>
        <div class="toast-message">{{ toastMessage() }}</div>
      </div>
    </div>
  </div>
}
